import {
  DATA_TERMINAL_TRANSITION_GRAPH_DEFAULT_FIELD,
  DATA_TERMINAL_TRANSITION_GRAPH_MAX_FIELDS,
  type DataTerminalTransitionGraphFieldItem,
} from './dataTerminalTransitionGraph';

/**
 * グラフ API を何回・どの項目で叩くかを決める（取得用）。
 * fields があればそれを使う。なければ field。どちらもなければ粗利合計(千円)（arariGk1000Base）。
 */
export const resolveDataTerminalTransitionGraphFields = (params: {
  field?: string;
  fields?: string[];
}): string[] => {
  if (params.fields && params.fields.length > 0) {
    return params.fields.slice(0, DATA_TERMINAL_TRANSITION_GRAPH_MAX_FIELDS);
  }
  if (params.field) {
    return [params.field];
  }
  return [DATA_TERMINAL_TRANSITION_GRAPH_DEFAULT_FIELD];
};

/**
 * 「推移グラフを表示」押下時に API へ送る項目一覧。
 * 既にグラフ表示済みなら graphList 由来の項目を維持し、初回のみデフォルト1本。
 */
export const resolveDataTerminalTransitionGraphFieldsForSearch = (
  existingGraphParams: { field?: string; fields?: string[] } | undefined
): string[] => {
  if (existingGraphParams) {
    return resolveDataTerminalTransitionGraphFields(existingGraphParams);
  }
  return [DATA_TERMINAL_TRANSITION_GRAPH_DEFAULT_FIELD];
};

/**
 * お気に入り適用時に表示するグラフ項目一覧を決める。
 */
export const resolveDataTerminalTransitionGraphFieldsFromFavorite = (setting: {
  field?: string;
  fields?: string[];
}): string[] => {
  return resolveDataTerminalTransitionGraphFields(setting);
};

export const buildGraphFieldsAfterAdd = (
  currentFields: readonly string[],
  newField: string,
  maxFields: number = DATA_TERMINAL_TRANSITION_GRAPH_MAX_FIELDS
): string[] => {
  if (currentFields.length >= maxFields) {
    return currentFields.slice(0, maxFields);
  }
  return [...currentFields, newField].slice(0, maxFields);
};

export const buildGraphFieldsAfterAddEmptySlot = (
  currentFields: readonly string[],
  maxFields: number = DATA_TERMINAL_TRANSITION_GRAPH_MAX_FIELDS
): string[] => buildGraphFieldsAfterAdd(currentFields, '', maxFields);

export const shouldRefetchDataTerminalTransitionGraphFields = (
  prevFields: readonly string[],
  nextFields: readonly string[]
): boolean => {
  const normalizedPrev = normalizeGraphFieldsForFetch([...prevFields]);
  const normalizedNext = normalizeGraphFieldsForFetch([...nextFields]);
  return normalizedPrev.join(',') !== normalizedNext.join(',');
};

const areRemainingGraphFieldsInOrder = (
  remainingFields: readonly string[],
  originalFields: readonly string[]
): boolean =>
  remainingFields.reduce<{ searchIndex: number; isMatch: boolean }>(
    (state, fieldCode) => {
      if (!state.isMatch) {
        return state;
      }
      const relativeIndex = originalFields
        .slice(state.searchIndex)
        .findIndex((candidate) => candidate === fieldCode);
      if (relativeIndex === -1) {
        return { searchIndex: state.searchIndex, isMatch: false };
      }
      return {
        searchIndex: state.searchIndex + relativeIndex + 1,
        isMatch: true,
      };
    },
    { searchIndex: 0, isMatch: true }
  ).isMatch;

export const isGraphFieldsRemovalOnly = (
  prevFields: readonly string[],
  nextFields: readonly string[]
): boolean => {
  const normalizedPrev = normalizeGraphFieldsForFetch([...prevFields]);
  const normalizedNext = normalizeGraphFieldsForFetch([...nextFields]);
  if (normalizedNext.length >= normalizedPrev.length) {
    return false;
  }
  return areRemainingGraphFieldsInOrder(normalizedNext, normalizedPrev);
};

export const buildGraphFieldsAfterRemove = (
  currentFields: readonly string[],
  index: number,
  maxFields: number = DATA_TERMINAL_TRANSITION_GRAPH_MAX_FIELDS
): string[] => {
  if (index < 0 || index >= currentFields.length || currentFields.length <= 1) {
    return currentFields.slice(0, maxFields);
  }
  return currentFields
    .filter((_, fieldIndex) => fieldIndex !== index)
    .slice(0, maxFields);
};

export const pickDefaultFieldForNewGraph = (
  currentFields: readonly string[],
  availableFields: readonly { code: string }[],
  fallback: string = DATA_TERMINAL_TRANSITION_GRAPH_DEFAULT_FIELD
): string => {
  const usedFieldCodes = new Set(currentFields.filter((field) => field !== ''));
  const unusedField = availableFields.find(
    (field) => !usedFieldCodes.has(field.code)
  );
  return unusedField?.code ?? availableFields.at(0)?.code ?? fallback;
};

export const buildGraphFieldsAfterIndexChange = (
  currentFields: readonly string[],
  index: number,
  field: string,
  maxFields: number = DATA_TERMINAL_TRANSITION_GRAPH_MAX_FIELDS
): string[] => {
  if (index < 0 || index >= maxFields) {
    return currentFields.slice(0, maxFields);
  }

  if (field === '') {
    const slotCount = Math.min(
      Math.max(currentFields.length, index + 1),
      maxFields
    );
    return Array.from({ length: slotCount }, (_, slotIndex) =>
      slotIndex === index ? '' : currentFields.at(slotIndex) ?? ''
    );
  }

  const slotCount = Math.min(
    Math.max(currentFields.length, index + 1),
    maxFields
  );

  return Array.from({ length: slotCount }, (_, slotIndex) =>
    slotIndex === index
      ? field
      : currentFields.at(slotIndex) ??
        DATA_TERMINAL_TRANSITION_GRAPH_DEFAULT_FIELD
  );
};

export const normalizeGraphFieldsForFetch = (
  fields: string[],
  maxFields: number = DATA_TERMINAL_TRANSITION_GRAPH_MAX_FIELDS
): string[] => {
  return fields.filter((field) => field !== '').slice(0, maxFields);
};

export const resolveGraphListIndexForFieldSlot = (
  fieldSlots: readonly string[],
  slotIndex: number
): number => {
  return fieldSlots.slice(0, slotIndex).filter((field) => field !== '').length;
};

export const resolveGraphItemForFieldSlot = (
  graphList: readonly DataTerminalTransitionGraphFieldItem[],
  fieldSlots: readonly string[],
  slotIndex: number
): DataTerminalTransitionGraphFieldItem | undefined => {
  const field = fieldSlots.at(slotIndex);
  if (field === undefined || field === '') {
    return undefined;
  }
  const graphListIndex = resolveGraphListIndexForFieldSlot(
    fieldSlots,
    slotIndex
  );
  const itemByIndex = graphList.at(graphListIndex);
  if (itemByIndex?.field === field) {
    return itemByIndex;
  }
  return graphList.find((graphItem) => graphItem.field === field);
};

export const buildGraphListForNormalizedFields = (
  currentGraphList: readonly DataTerminalTransitionGraphFieldItem[],
  normalizedFields: readonly string[]
): DataTerminalTransitionGraphFieldItem[] => {
  const graphByField = new Map(
    currentGraphList.map((item) => [item.field, item])
  );
  return normalizedFields.flatMap((field) => {
    const item = graphByField.get(field);
    return item ? [item] : [];
  });
};

export const mergeFieldSlotsFromGraphList = (
  prevSlots: readonly string[],
  graphList: readonly Pick<DataTerminalTransitionGraphFieldItem, 'field'>[],
  maxFields: number = DATA_TERMINAL_TRANSITION_GRAPH_MAX_FIELDS
): string[] => {
  if (graphList.length === 0) {
    return [];
  }

  const fieldsFromGraph = graphList.map((item) => item.field);
  const normalizedPrev = normalizeGraphFieldsForFetch(
    [...prevSlots],
    maxFields
  );

  if (normalizedPrev.join(',') === fieldsFromGraph.join(',')) {
    return prevSlots.slice(0, maxFields);
  }

  if (
    normalizedPrev.length > fieldsFromGraph.length &&
    fieldsFromGraph.every((field, index) => normalizedPrev.at(index) === field)
  ) {
    return prevSlots.slice(0, maxFields);
  }

  const trailingEmptySlots: string[] = [];
  for (let index = fieldsFromGraph.length; index < prevSlots.length; index++) {
    if (prevSlots.at(index) === '') {
      trailingEmptySlots.push('');
    } else {
      break;
    }
  }

  return [...fieldsFromGraph, ...trailingEmptySlots].slice(0, maxFields);
};

export { syncGraphSlotKeys } from './graphSlotKeys';
